import dayjs, { type Dayjs, type PluginFunc } from "dayjs";
import enLocale from "dayjs/locale/en";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localizedFormat from "dayjs/plugin/localizedFormat";
import objectSupport from "dayjs/plugin/objectSupport";
import quarter from "dayjs/plugin/quarterOfYear";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);
dayjs.extend(objectSupport);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(quarter);

declare module "dayjs" {
  interface Dayjs {
    fromNowDetailed(isShort?: boolean): string;
  }
}

type FromToBaseType = {
  isShort: boolean;
  instance: Dayjs;
  isFrom?: boolean;
  postFormat?: (x: string) => string;
};

const longRelObj = {
  future: "in %s",
  past: "%s ago",
  s: "few seconds",
  m: "1 minute",
  mm: "%d minutes",
  h: "1 hour",
  hh: "%d hours",
  d: "1 day",
  dd: "%d days",
  M: "1 month",
  MM: "%d months",
  y: "1 year",
  yy: "%d years",
};

const shortRelObj = {
  future: "in %s",
  past: "%s ago",
  s: "seconds",
  m: "1min",
  mm: "%dmin",
  h: "1h",
  hh: "%dh",
  d: "1d",
  dd: "%dd",
  M: "1m",
  MM: "%dm",
  y: "1yr",
  yy: "%dyr",
};

export const relativeTimeDetailedPlugin: PluginFunc<any> = (
  _option,
  dayjsClass,
  dayjsFactory,
) => {
  const fromToBase = ({ isShort, instance }: FromToBaseType): string => {
    const and = isShort ? " " : " and ";

    // Update the locale to use the appropriate relative time strings
    dayjsFactory.locale("en", {
      ...enLocale,
      relativeTime: isShort ? shortRelObj : longRelObj,
    });

    const now = dayjsFactory();
    const inputDate = dayjsFactory(instance);

    const duration = dayjsFactory.duration(inputDate.diff(now));

    // These values are whole numbers of each interval format
    const durationInYears = duration.years();
    const durationInMonths = duration.months();
    const durationInDays = duration.days();

    // We don't want to add suffixes to this variable since it will be used as the first part of modified outputs
    const relativeDate = inputDate.fromNow(true);

    // Modify the years output if there is a year with at least 1 month
    // Using !== 0 because values can be negative
    if (durationInYears !== 0 && durationInMonths !== 0) {
      // Add the remaining months from the current date to get the leftover relative time
      // We use add() instead of subtract() because the duration variable can be negative if necessary
      const relativeLeftoverMonths = now
        .add(durationInMonths, "months")
        .fromNow(isShort);
      return relativeDate + and + relativeLeftoverMonths;
    }

    // Modify the months output if there is a month with at least 1 day
    if (durationInMonths !== 0 && durationInDays !== 0) {
      const relativeLeftoverDays = now
        .add(durationInDays, "days")
        .fromNow(isShort);
      return relativeDate + and + relativeLeftoverDays;
    }

    return inputDate.fromNow(isShort);
  };

  dayjsClass.prototype.fromNowDetailed = function (isShort = false) {
    return fromToBase({
      isShort,
      instance: this,
    });
  };
};
